import { Component, OnDestroy, HostListener, OnInit, ChangeDetectorRef, inject, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'app/services/utilities/cookie.service';
import { RegisterState } from '../models/RegisterState';
import { PaymentData } from '../models/PaymentData';
import { MethodApi } from '../api/api-request';
import { SubscriptionBundle } from '../models/SubscriptionBundle';
import { SubscriptionResponse } from '../models/SubscriptionResponse';
import { MatDialog } from '@angular/material/dialog';
import { DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import { ProcessingPaymentDialogComponent } from './process-payment-dialog/process-payment-dialog.component';
import { NetworkCoverage } from 'app/models/NetworkCoverage';
import { DialogConversionComponent } from 'app/dialog/dialog-conversion/dialog-conversion.component';
import { Discount, Option, Subscription, GeneralUser, ItemConfig } from '@co-assist/library';
import { LoadingService } from '@app/services/utilities/loading.service';

@Component({
    selector: 'app-subscription',
    templateUrl: './subscription.component.html',
    styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit, OnDestroy {
    registerState: RegisterState;
    subscriptionBundle: SubscriptionBundle;
    isLinear: boolean = true;
    isEditable: boolean = true; // step header selection is disabled in CSS
    isLoading: WritableSignal<boolean> = signal(true);
    private _overlayService = inject(LoadingService);
    constructor(
        private api: MethodApi,
        private translate: TranslateService,
        private dialog: MatDialog,
        private cookie: CookieService,
        private cdRef: ChangeDetectorRef
    ) { }

    ngOnInit() {
        this.getRegisterState();
        this.loadHubspotChatScript();
        this.detectUserLeavingPage();
    }

    /**
     * When we quit the component all the registerState object is stored into cookies call 'registerState'
     */
    ngOnDestroy(): void {
        if (this.registerState) {
            this.registerState.subscriptionBundle = this.subscriptionBundle;
            this.cookie.set('registerState', JSON.stringify(this.registerState));
        }
    }

    detectUserLeavingPage() {
        const launchPage = (event) => {
            const userLeavesFromTop: boolean = event.clientY <= 0;
            if (userLeavesFromTop) {
                this.displayContactForm();
                document.removeEventListener('mouseleave', launchPage);
            }
        };
        document.addEventListener('mouseleave', launchPage);
    }

    displayContactForm() {
        this.dialog.open(DialogConversionComponent, {
            disableClose: false
        });
    }

    /**
     * Retrieve if existent the registerState stored in cookies
     */
    getRegisterState(): void {
        let cookie = this.cookie.get('registerState');
        if (cookie.length > 0) {
            this.dialog.open(DialogConfirmationComponent, {
                disableClose: true,
                data: {
                    title: this.translate.instant('REGISTRATION.STEP.PAYMENT.DIALOG_RECOVER_DATA.TITLE'),
                    content: this.translate.instant('REGISTRATION.STEP.PAYMENT.DIALOG_RECOVER_DATA.MESSAGE'),
                    isConfirmation: true
                }
            }).afterClosed().subscribe(result => {
                if (result) {
                    this.registerState = new RegisterState(JSON.parse(cookie));
                    this.subscriptionBundle = new SubscriptionBundle(this.registerState.subscriptionBundle);
                    this.updateSectionView(this.registerState.currentSection);
                    this.cdRef.markForCheck()
                    this.isLoading.set(false);
                } else {
                    this.cookie.delete('registerState');
                    this.registerState = new RegisterState();
                    this.subscriptionBundle = new SubscriptionBundle(this.registerState.subscriptionBundle);
                    this.cdRef.markForCheck()
                    this.isLoading.set(false);
                }
            });
        } else {
            this.registerState = new RegisterState();
            this.subscriptionBundle = new SubscriptionBundle(this.registerState.subscriptionBundle);
            this.isLoading.set(false);
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeunloadHandler() {
        this.registerState.subscriptionBundle = this.subscriptionBundle;
        if (!this.registerState.isEmpty()) {
            this.cookie.set('registerState', JSON.stringify(this.registerState));
        }
    }

    nextPageFrom(page: { section: string, direction: string }): void {
        const sectionIndex = this.registerState.sectionOrder.indexOf(page.section);
        if (sectionIndex < this.registerState.sectionOrder.length && page.direction === 'continue') {
            this.registerState.sectionCompleted[sectionIndex] = true;
        }
        setTimeout(() => {
            this.registerState.currentSection = page.direction === 'continue' ?
                this.nextSection(page.section) :
                this.previousSection(page.section);
            this.cdRef.markForCheck();
        }, 1);
    }

    updateSectionView(section: number): void {
        this.nextSection(this.registerState.sectionOrder[section], true);
    }

    updateCoverage(coverage: NetworkCoverage): void {
        this.subscriptionBundle.coverage = coverage;
    }

    updateOptions(options: Array<Option>): void {
        this.subscriptionBundle.options = options;
    }

    updateItemConfig(item: ItemConfig): void {
        this.subscriptionBundle.itemConfig = item;
    }

    updateRecipientInfo(obj: any): void {
        this.subscriptionBundle.recipientInformation.recipientAddress = obj.recipientAddress;
        this.subscriptionBundle.recipientInformation.recipientFirstName = obj.recipientFirstName;
        this.subscriptionBundle.recipientInformation.recipientLastName = obj.recipientLastName;
    }

    updateDiscount(discount: Discount): void {
        this.subscriptionBundle.discount = discount;
    }

    updateHelper(helper: GeneralUser): void {
        this.subscriptionBundle.helper = helper;
    }

    updateHelped(helped: GeneralUser): void {
        this.subscriptionBundle.helped = helped;
    }

    updateSubscription(subscription: Subscription): void {
        this.subscriptionBundle.subscription = subscription;
        if (subscription.name === 'NONE') {
            this.dialog.open(DialogConfirmationComponent, {
                disableClose: true,
                data: {
                    title: this.translate.instant('REGISTRATION.STEP.PAYMENT.ALREADY_OWN_WATCH.DIALOG.TITLE'),
                    content: this.translate.instant('REGISTRATION.STEP.PAYMENT.ALREADY_OWN_WATCH.DIALOG.CONTENT'),
                    isConfirmation: true
                }
            }).afterClosed().subscribe(result => {
                if (result === true) {
                    this.updatePaymentData(this.subscriptionBundle.paymentData);
                }
            });
        }
    }

    updateAdditionalInfo(info: string): void {
        this.subscriptionBundle.additionalInfo = info;
    }

    updateTaxCreditForSubscription(taxCredit: boolean): void {
        this.subscriptionBundle.taxCredit = taxCredit;
    }

    updatePaymentData(paymentData: PaymentData): void {
        this.subscriptionBundle.paymentData = paymentData;
        this._overlayService.showRaw('Paiement en cours');

        this.api.subscriptionRequest(this.subscriptionBundle).subscribe({
            next: response => {
                const subscriptionResponse = new SubscriptionResponse(response);
                let data = {
                    isAccountVerified: false,
                    isPaymentAuthorise: false,
                    isAuthorisationRequired: false,
                    isLoading: false,
                    is400: false,
                    is500: false,
                    errorMessage: '',
                    successMessage: '',
                    subscriptionName: this.subscriptionBundle.subscription.name,
                    isHelped: response.isHelped,
                    returnUrl: ''
                };
                this.updateCookieWithCreationInformations(subscriptionResponse);
                if (subscriptionResponse.need3DSecureValidation()) {
                    data.isAuthorisationRequired = true;
                    data.successMessage = this.translate.instant(
                        'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.3D_SECURE'
                    );
                    data.returnUrl = subscriptionResponse.get3DSecureLink(); // 3DSecure Page
                } else if (
                    subscriptionResponse.isValidated() ||
                    subscriptionResponse.isTokenized()
                ) {
                    this.cookie.delete('registerState');
                    data.isPaymentAuthorise = true;
                    data.successMessage =
                        this.subscriptionBundle.subscription.name === 'NONE'
                            ? this.translate.instant(
                                'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.SUCCESS'
                            )
                            : this.translate.instant(
                                'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.SUCCESS_PAYMENT'
                            );
                } else if (subscriptionResponse.isSepa() && subscriptionResponse.isValidated()) {
                    this.cookie.delete('registerState');
                    data.isAuthorisationRequired = true;
                    data.successMessage = this.translate.instant(
                        'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.SUCCESS'
                    );
                } else {
                    // Error case
                    data.errorMessage = this.translate.instant(
                        'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.ERROR'
                    );
                }
                this.displayResponse(data);
            },
            error: (err) => {
                const error400 = '[400] Bad Request: ';
                let errorMessage;
                if (err?.error?.errorMessage.startsWith(error400)) {
                    errorMessage = err?.error?.errorMessage.split(error400)[1];
                    switch (errorMessage) {
                        case 'Invalid Payment data':
                            errorMessage = this.translate.instant(
                                'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.ERROR.INVALID_PAYMENT_DATA'
                            );
                            break;
                    }
                    this.displayResponse({
                        isAccountVerified: false,
                        isAuthorisationRequired: false,
                        isPaymentAuthorise: false,
                        isLoading: false,
                        is400: true,
                        is500: false,
                        errorMessage: errorMessage,
                        successMessage: '',
                        isHelped: false
                    })
                } else {
                    errorMessage = this.translate.instant(
                        'REGISTRATION.STEP.PAYMENT.DIALOG_PAYMENT.ERROR.MESSAGE'
                    );
                    this.displayResponse({
                        isAccountVerified: false,
                        isAuthorisationRequired: false,
                        isPaymentAuthorise: false,
                        isLoading: false,
                        is400: false,
                        is500: true,
                        errorMessage: errorMessage,
                        successMessage: '',
                        isHelped: false
                    })
                }
            }
        });
    }

    private displayResponse(data) {
        this._overlayService.hide();
        this.dialog.open(ProcessingPaymentDialogComponent, {
            disableClose: true,
            data
        })
    }

    downloadBooklet(): void {
        let brochureUrl = 'https://www.co-assist.fr/contact';
        window.open(brochureUrl, '_blank');
    }

    /**
     * script can't be insert directy into html page, we must load them programatically
     * original script : <script type="text/javascript" id="hs-script-loader" async defer src="//js.hs-scripts.com/2276721.js"></script>
     */
    loadHubspotChatScript() {
        const node = document.createElement('script');
        node.src = '//js.hs-scripts.com/2276721.js';
        node.type = 'text/javascript';
        node.async = true;
        node.defer = true;
        document.getElementsByTagName('head')[0].appendChild(node);
    }

    private nextSection(page: string, reload: boolean = false): number {
        if (
            this.registerState.sectionOrder.indexOf(page) ===
            this.registerState.sectionOrder.length
        ) {
            return;
        } else {
            return this.registerState.sectionOrder.indexOf(page) + (reload ? 0 : 1);
        }
    }

    private previousSection(page: string): number {
        if (this.registerState.sectionOrder.indexOf(page) === 0) {
            return;
        } else {
            return this.registerState.sectionOrder.indexOf(page) - 1;
        }
    }

    private updateCookieWithCreationInformations(response: SubscriptionResponse) {
        if (!response.isHelped) {
            this.subscriptionBundle.helper.userID =
                response.helperUserID;
        }
        this.subscriptionBundle.helped.userID =
            response.helpedUserID;
        this.registerState.subscriptionBundle = this.subscriptionBundle;
        this.cookie.set('registerState', JSON.stringify(this.registerState));
    }
}
